import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Mail, Loader2 } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { useToast } from '../../components/ui/Toast';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useOutstandingPayments, type FamilyGroup, type SubscriptionWithAmountDue } from '../../hooks/useOutstandingPayments';
import { useRecordPayment, type RecordPaymentParams } from '../../hooks/useRecordPayment';
import { useAddLateFee } from '../../hooks/useAddLateFee';
import { supabase } from '../../lib/supabase';

export default function ManualPaymentsPage() {
  const navigate = useNavigate();
  const { data: currentCompanyId, role } = useCurrentCompany();
  const recordPaymentMutation = useRecordPayment();
  const addLateFeeMutation = useAddLateFee();
  const { showToast, ToastContainer } = useToast();
  const queryClient = useQueryClient();
  const [sendingInvoiceFor, setSendingInvoiceFor] = useState<string | null>(null);

  const isOwner = role === 'owner';
  const companyId = currentCompanyId ?? null;

  const [_selectedEnrollments, _setSelectedEnrollments] = useState<Set<string>>(new Set());
  const [selectedFamilies, setSelectedFamilies] = useState<Set<string>>(new Set());
  const [paymentAmounts, setPaymentAmounts] = useState<Record<string, string>>({});
  const [lateFeeAmounts, setLateFeeAmounts] = useState<Record<string, string>>({});
  const [activeAction, setActiveAction] = useState<Record<string, 'payment' | 'lateFee' | null>>({});
  const [isApplyingBulk, setIsApplyingBulk] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());

  // Query for subscriptions with amounts due (grouped by family)
  const { data: familyGroups, isLoading: isLoadingSubscriptions } = useOutstandingPayments({
    companyId: companyId ?? null,
    enabled: !!companyId && isOwner,
  });

  // Get all subscriptions from family groups for "Check All" functionality
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _allSubscriptions = familyGroups?.flatMap(group => group.subscriptions) || [];

  // Sync "Check All" state when families or selections change
  useEffect(() => {
    if (familyGroups && familyGroups.length > 0) {
      setIsAllSelected(selectedFamilies.size === familyGroups.length && selectedFamilies.size > 0);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedFamilies, familyGroups]);

  const formatCurrencyInput = (text: string): string => {
    let cleaned = text.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts[0] && parts[0].length > 5) {
      cleaned = parts[0].slice(0, 5) + (parts[1] ? '.' + parts[1] : '');
    }
    if (parts[1] && parts[1].length > 2) {
      cleaned = parts[0] + '.' + parts[1].slice(0, 2);
    }
    return cleaned;
  };

  const formatCurrencyDisplay = (value: string): string => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toFixed(2);
  };

  const toggleFamilySelection = (billingAccountId: string) => {
    const newSelected = new Set(selectedFamilies);
    if (newSelected.has(billingAccountId)) {
      newSelected.delete(billingAccountId);
    } else {
      newSelected.add(billingAccountId);
    }
    setSelectedFamilies(newSelected);
  };

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedFamilies(new Set());
      setIsAllSelected(false);
    } else {
      const allFamilyIds = new Set(familyGroups?.map(fg => fg.billing_account_id) || []);
      setSelectedFamilies(allFamilyIds);
      setIsAllSelected(true);
    }
  };

  const toggleFamilyExpansion = (billingAccountId: string) => {
    const newExpanded = new Set(expandedFamilies);
    if (newExpanded.has(billingAccountId)) {
      newExpanded.delete(billingAccountId);
    } else {
      newExpanded.add(billingAccountId);
    }
    setExpandedFamilies(newExpanded);
  };

  const handleFillFamilyDueAmount = (billingAccountId: string) => {
    const familyGroup = familyGroups?.find(fg => fg.billing_account_id === billingAccountId);
    if (familyGroup) {
      const amount = familyGroup.current_due.toFixed(2);
      setPaymentAmounts({
        ...paymentAmounts,
        [billingAccountId]: amount,
      });
    }
  };

  const handleApplyFamilyPayment = async (familyGroup: FamilyGroup, amount?: number) => {
    const paymentAmount = amount || familyGroup.current_due;
    if (paymentAmount <= 0) {
      alert('Invalid Amount: Payment amount must be greater than 0');
      return;
    }

    if (paymentAmount > familyGroup.current_due) {
      alert(`Amount Exceeds Balance: Maximum payment is $${familyGroup.current_due.toFixed(2)}`);
      return;
    }

    const totalOutstanding = familyGroup.subscriptions.reduce(
      (sum, s) => sum + s.outstanding_balance,
      0,
    );

    setIsApplyingBulk(true);
    try {
      const promises = familyGroup.subscriptions.map(subscription => {
        const proportion = subscription.outstanding_balance / totalOutstanding;
        const subscriptionPayment = Math.min(
          paymentAmount * proportion,
          subscription.outstanding_balance,
        );

        if (subscriptionPayment <= 0) return Promise.resolve();

        const params: RecordPaymentParams = {
          enrollmentId: subscription.enrollment_id,
          amount: subscriptionPayment,
          paymentMethod: 'cash',
          billingDate: subscription.billing_date,
          notes: `Family payment (${familyGroup.family_lead_last_name})`,
        };

        return recordPaymentMutation.mutateAsync(params);
      });

      await Promise.all(promises);
      showToast(`Payment applied to ${familyGroup.subscription_count} subscription(s)`, 'success');
      setActiveAction(prev => {
        const updated = { ...prev };
        delete updated[familyGroup.billing_account_id];
        return updated;
      });
      setPaymentAmounts(prev => {
        const updated = { ...prev };
        delete updated[familyGroup.billing_account_id];
        return updated;
      });
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to apply family payment', 'error');
    } finally {
      setIsApplyingBulk(false);
    }
  };

  const handleAddFamilyLateFee = async (familyGroup: FamilyGroup, amount?: number) => {
    const lateFeeAmount = amount || 0;
    if (lateFeeAmount <= 0) {
      showToast('Invalid Amount: Late fee amount must be greater than 0', 'error');
      return;
    }

    const lateSubscriptions = familyGroup.subscriptions.filter(
      sub => sub.late_fees > 0 || (sub.due_date && new Date(sub.due_date) < new Date()),
    );

    if (lateSubscriptions.length === 0) {
      showToast('No Late Subscriptions: This family has no late subscriptions', 'error');
      return;
    }

    if (
      !confirm(
        `This family has ${lateSubscriptions.length} late subscription(s). Apply late fee to all?`,
      )
    ) {
      return;
    }

    setIsApplyingBulk(true);
    try {
      const promises = lateSubscriptions.map(sub =>
        addLateFeeMutation.mutateAsync({
          enrollmentId: sub.enrollment_id,
          amount: lateFeeAmount,
          daysLate: 0,
        }),
      );
      await Promise.all(promises);
      showToast(`Late fee applied to ${lateSubscriptions.length} subscription(s)`, 'success');
      setActiveAction(prev => {
        const updated = { ...prev };
        delete updated[familyGroup.billing_account_id];
        return updated;
      });
      setLateFeeAmounts(prev => {
        const updated = { ...prev };
        delete updated[familyGroup.billing_account_id];
        return updated;
      });
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to add late fees', 'error');
    } finally {
      setIsApplyingBulk(false);
    }
  };

  // Send invoice mutation
  const sendInvoiceMutation = useMutation({
    mutationFn: async ({ 
      billingScheduleId, 
      paymentRecordId, 
      enrollmentId, 
      amount, 
      dueDate 
    }: { 
      billingScheduleId?: string | null
      paymentRecordId?: string | null
      enrollmentId: string
      amount?: number
      dueDate?: string
    }) => {
      const { data, error } = await supabase.functions.invoke('send-invoice', {
        body: {
          companyId,
          ...(billingScheduleId && { billingScheduleId }),
          ...(paymentRecordId && { paymentRecordId }),
          ...(enrollmentId && !billingScheduleId && !paymentRecordId && {
            enrollmentId,
            amount,
            dueDate,
          }),
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      showToast('Invoice sent successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['subscriptionsWithAmountDue'] });
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to send invoice', 'error');
    },
    onSettled: () => {
      setSendingInvoiceFor(null);
    },
  });

  const handleSendInvoice = async (subscription: SubscriptionWithAmountDue) => {
    setSendingInvoiceFor(subscription.enrollment_id);
    
    try {
      await sendInvoiceMutation.mutateAsync({
        billingScheduleId: subscription.billing_schedule_id || null,
        paymentRecordId: subscription.payment_record_id || null,
        enrollmentId: subscription.enrollment_id,
        ...(!subscription.billing_schedule_id && !subscription.payment_record_id && {
          amount: subscription.outstanding_balance,
          dueDate: subscription.due_date || new Date().toISOString().split('T')[0],
        }),
      });
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleBulkPayment = async () => {
    if (selectedFamilies.size === 0) {
      showToast('No Selection: Please select at least one family to apply payment', 'error');
      return;
    }

    setIsApplyingBulk(true);
    try {
      const promises: Promise<unknown>[] = [];

      familyGroups?.forEach(familyGroup => {
        if (selectedFamilies.has(familyGroup.billing_account_id)) {
          familyGroup.subscriptions.forEach(subscription => {
            const paymentAmount = subscription.outstanding_balance;
            if (paymentAmount > 0) {
              const params: RecordPaymentParams = {
                enrollmentId: subscription.enrollment_id,
                amount: paymentAmount,
                paymentMethod: 'cash',
                billingDate: subscription.billing_date,
                notes: `Bulk family payment (${familyGroup.family_lead_last_name})`,
              };
              promises.push(recordPaymentMutation.mutateAsync(params));
            }
          });
        }
      });

      await Promise.all(promises);
      showToast(`Payment applied to ${selectedFamilies.size} family/families`, 'success');
      setSelectedFamilies(new Set());
      setIsAllSelected(false);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to apply bulk payment', 'error');
    } finally {
      setIsApplyingBulk(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p className="text-white/80 mb-6">Owners only.</p>
        </div>
      </div>
    );
  }

  const totalOutstanding = familyGroups?.reduce((sum, f) => sum + f.current_due, 0) || 0;
  const selectedTotal = Array.from(selectedFamilies)
    .map(id => familyGroups?.find(f => f.billing_account_id === id)?.current_due || 0)
    .reduce((sum, amount) => sum + amount, 0);

  return (
    <>
      <ToastContainer />
      <div className="w-full min-h-[calc(100vh-5rem)] flex flex-col bg-[#111010] pt-6">
        {/* Zero Fee Savings Banner */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <div className="text-rollops-accent mt-0.5">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-[#EBFFEE] font-semibold mb-1">Save on Credit Card Processing</h3>
                <p className="text-[#A9A3A3] text-sm mb-3">
                  Zero fee billing can save you whatever you&apos;re paying in credit card processing fees.
                  Within a year, you can make that 0% out of your bottom line by trusting RollOps as
                  your platform.
                </p>
                <button
                  onClick={() => navigate('/billing/settings')}
                  className="text-rollops-accent hover:text-[#EBFFEE] text-sm font-semibold underline"
                >
                  Set up Zero Fee Stripe Configuration →
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-[#A9A3A3] mb-1">Total Outstanding</p>
                <p className="text-2xl font-bold text-[#EBFFEE]">${totalOutstanding.toFixed(2)}</p>
              </div>
              {selectedFamilies.size > 0 && (
                <div className="text-right">
                  <p className="text-sm text-[#A9A3A3] mb-1">Selected Total</p>
                  <p className="text-2xl font-bold text-rollops-accent">${selectedTotal.toFixed(2)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Check All */}
        {familyGroups && familyGroups.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={toggleSelectAll}
              className="w-5 h-5 rounded border-[#504A4A] bg-[#363232] text-text-primary focus:ring-[#9B1421]"
            />
            <label className="text-[#EBFFEE] font-semibold cursor-pointer">Check All Families</label>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedFamilies.size > 0 && (
          <div className="mb-6">
            <Button
              onClick={handleBulkPayment}
              disabled={isApplyingBulk}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] w-full"
            >
              {isApplyingBulk ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Processing...
                </span>
              ) : (
                `Mark ${selectedFamilies.size} Family/Families Paid`
              )}
            </Button>
          </div>
        )}

        {/* Family Groups List */}
        {isLoadingSubscriptions ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
            <span className="ml-3 text-[#EBFFEE]">Loading subscriptions...</span>
          </div>
        ) : familyGroups && familyGroups.length > 0 ? (
          <div className="space-y-4">
            {familyGroups.map(familyGroup => {
              const isExpanded = expandedFamilies.has(familyGroup.billing_account_id);
              const billingAccountId = familyGroup.billing_account_id;
              const isFamilySelected = selectedFamilies.has(billingAccountId);
              const familyPaymentAmount = paymentAmounts[billingAccountId] || '';
              const familyLateFeeAmount = lateFeeAmounts[billingAccountId] || '';
              const familyActiveAction = activeAction[billingAccountId];

              return (
                <Card key={billingAccountId} className="bg-[#221F1F] border-[#504A4A]">
                  <CardContent className="p-4">
                    {/* Family Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isFamilySelected}
                          onChange={() => toggleFamilySelection(billingAccountId)}
                          className="w-5 h-5 rounded border-[#504A4A] bg-[#363232] text-text-primary focus:ring-[#9B1421]"
                        />
                        <button
                          onClick={() => toggleFamilyExpansion(billingAccountId)}
                          className="flex items-center gap-2 flex-1 text-left"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-[#EBFFEE]" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-[#EBFFEE]" />
                          )}
                          <div>
                            <h3 className="text-lg font-bold text-[#EBFFEE]">
                              Family ({familyGroup.family_lead_last_name})
                            </h3>
                            <p className="text-sm text-[#A9A3A3]">
                              {familyGroup.subscription_count} subscription
                              {familyGroup.subscription_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#A9A3A3] mb-1">Total Due</p>
                        <p className="text-xl font-bold text-[#EBFFEE]">
                          ${familyGroup.total_due.toFixed(2)}
                        </p>
                        <p className="text-xs text-[#A9A3A3] mt-1">Current Due</p>
                        <p className="text-lg font-bold text-rollops-accent">
                          ${familyGroup.current_due.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Family Action Container */}
                    {familyGroup.current_due > 0 && (
                      <div className="border-t border-[#504A4A] pt-4">
                        {!familyActiveAction ? (
                          <div className="flex gap-2">
                            <Button
                              onClick={() =>
                                setActiveAction({ ...activeAction, [billingAccountId]: 'payment' })
                              }
                              disabled={isApplyingBulk}
                              className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
                            >
                              Apply Payment
                            </Button>
                            <Button
                              onClick={() =>
                                setActiveAction({ ...activeAction, [billingAccountId]: 'lateFee' })
                              }
                              disabled={isApplyingBulk}
                              className="flex-1 bg-red-600 hover:bg-red-700 text-[#EBFFEE]"
                            >
                              Add Late Fee
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Enter amount"
                                maxLength={10}
                                value={
                                  familyActiveAction === 'payment'
                                    ? familyPaymentAmount
                                    : familyLateFeeAmount
                                }
                                onChange={e => {
                                  const formatted = formatCurrencyInput(e.target.value);
                                  if (familyActiveAction === 'payment') {
                                    setPaymentAmounts({
                                      ...paymentAmounts,
                                      [billingAccountId]: formatted,
                                    });
                                  } else {
                                    setLateFeeAmounts({
                                      ...lateFeeAmounts,
                                      [billingAccountId]: formatted,
                                    });
                                  }
                                }}
                                onBlur={() => {
                                  const value =
                                    familyActiveAction === 'payment'
                                      ? familyPaymentAmount
                                      : familyLateFeeAmount;
                                  const formatted = formatCurrencyDisplay(value);
                                  if (familyActiveAction === 'payment') {
                                    setPaymentAmounts({
                                      ...paymentAmounts,
                                      [billingAccountId]: formatted,
                                    });
                                  } else {
                                    setLateFeeAmounts({
                                      ...lateFeeAmounts,
                                      [billingAccountId]: formatted,
                                    });
                                  }
                                }}
                                className="flex-1 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                              />
                              {familyActiveAction === 'payment' && (
                                <Button
                                  onClick={() => handleFillFamilyDueAmount(billingAccountId)}
                                  className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
                                >
                                  + Due
                                </Button>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => {
                                  setActiveAction(prev => {
                                    const updated = { ...prev };
                                    delete updated[billingAccountId];
                                    return updated;
                                  });
                                }}
                                className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  if (familyActiveAction === 'payment') {
                                    const amount = parseFloat(familyPaymentAmount);
                                    if (isNaN(amount) || amount <= 0) {
                                      showToast('Please enter a valid payment amount', 'error');
                                      return;
                                    }
                                    handleApplyFamilyPayment(familyGroup, amount);
                                  } else {
                                    const amount = parseFloat(familyLateFeeAmount);
                                    if (isNaN(amount) || amount <= 0) {
                                      showToast('Please enter a valid late fee amount', 'error');
                                      return;
                                    }
                                    handleAddFamilyLateFee(familyGroup, amount);
                                  }
                                }}
                                disabled={isApplyingBulk}
                                className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
                              >
                                {isApplyingBulk ? 'Processing...' : 'Apply'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Individual Subscriptions (when expanded) */}
                    {isExpanded && (
                      <div className="mt-4 space-y-3 border-t border-[#504A4A] pt-4">
                        {familyGroup.subscriptions.map(subscription => (
                          <div
                            key={subscription.enrollment_id}
                            className="bg-[#363232] rounded-lg p-3 border border-[#504A4A]"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-semibold text-[#EBFFEE]">
                                  {subscription.student_name}
                                </p>
                                <p className="text-sm text-[#A9A3A3]">{subscription.subscription_name}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-[#A9A3A3] mb-1">Amount Due</p>
                                <p className="text-lg font-bold text-[#EBFFEE]">
                                  ${subscription.outstanding_balance.toFixed(2)}
                                </p>
                                {subscription.due_date && (
                                  <p className="text-xs text-[#A9A3A3] mt-1">
                                    Due: {new Date(subscription.due_date).toLocaleDateString()}
                                  </p>
                                )}
                                {subscription.late_fees > 0 && (
                                  <p className="text-xs text-red-400 mt-1">
                                    Late Fees: ${subscription.late_fees.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            </div>
                            {/* Send Invoice Button */}
                            <div className="mt-3 pt-3 border-t border-[#504A4A]">
                              <Button
                                onClick={() => handleSendInvoice(subscription)}
                                disabled={sendingInvoiceFor === subscription.enrollment_id}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-[#EBFFEE] text-sm"
                              >
                                {sendingInvoiceFor === subscription.enrollment_id ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Sending...
                                  </span>
                                ) : (
                                  <span className="flex items-center justify-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    Send Invoice
                                  </span>
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-[#EBFFEE] mb-2">
                All subscriptions are up to date!
              </p>
              <p className="text-sm text-[#A9A3A3]">No outstanding payments at this time</p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
